import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useNotifications } from '../../../../hooks/notifications/useNotifications';
import { useDropdown } from '../../../../hooks/notifications/useDropdown';
import './NotificationBell.css';

const NotificationBell = ({ userInfo }) => {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications(userInfo);
  const { isOpen, dropdownRef, toggleDropdown, closeDropdown } = useDropdown(markAllAsRead);

  /** Parse notification.data (API returns JSON string). */
  const getNotificationData = (notification) => {
    if (!notification?.data) return null;
    if (typeof notification.data === 'object') return notification.data;
    try {
      return JSON.parse(notification.data);
    } catch {
      return null;
    }
  };

  /**
   * Return redirect path for a notification, or null if no redirect.
   * Backend stores type-specific payload in notification.data (e.g. jobPostId, applicationId, conversationId).
   */
  const getRedirectPath = (notification) => {
    const data = getNotificationData(notification);
    if (!data) return null;

    switch (notification.type) {
      case 'job_application':
        if (data.jobPostId != null) return `/panel-empresa/trabajos/${data.jobPostId}/postulantes`;
        if (data.applicationId != null) return `/panel-empresa/trabajos/postulantes/${data.applicationId}`;
        return null;
      case 'message':
      case 'MESSAGE':
        if (data.conversationId != null) return `/panel-empresa/mensajes`;
        return null;
      default:
        return null;
    }
  };

  const handleNotificationClick = (notification) => {
    const path = getRedirectPath(notification);
    if (path) {
      if (!notification.isRead) markAsRead(notification.id);
      closeDropdown();
      navigate(path);
    }
  };


  // Format time ago
  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Hace un momento';
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} h`;
    if (diffInSeconds < 2592000) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;
    return date.toLocaleDateString();
  };

  // Get notification icon and color
  const getNotificationStyle = (type) => {
    switch (type) {
      case 'job_application':
        return { icon: '📝', color: '#4CAF50' };
      case 'message':
      case 'MESSAGE':
        return { icon: '💬', color: '#2196F3' };
      case 'job_offer':
        return { icon: '💼', color: '#FF9800' };
      case 'scheduled_call':
      case 'scheduled_call_reminder_email':
        return { icon: '📞', color: '#fb5424' };
      case 'system':
        return { icon: '🔔', color: '#9C27B0' };
      default:
        return { icon: '🔔', color: '#666' };
    }
  };


  
  if (!userInfo?.userId) {
    return null;
  }

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className={`notification-bell ${unreadCount > 0 ? 'has-notifications' : ''}`}
        onClick={toggleDropdown}
        aria-label={`Notificaciones ${unreadCount > 0 ? `(${unreadCount} sin leer)` : ''}`}
      >
        <FontAwesomeIcon icon={faBell} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notificaciones</h3>
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-btn"
                onClick={markAllAsRead}
                title="Marcar todas como leídas"
              >
                <FontAwesomeIcon icon={faCheck} />
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">
                <div className="loading-spinner"></div>
                <span>Cargando notificaciones...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <FontAwesomeIcon icon={faBell} />
                <span>No tienes notificaciones</span>
              </div>
            ) : (
              notifications.map((notification) => {
                const style = getNotificationStyle(notification.type);
                const redirectPath = getRedirectPath(notification);
                const isClickable = Boolean(redirectPath);
                return (
                  <div
                    key={notification.id}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    className={`notification-item ${!notification.isRead ? 'unread' : ''} ${isClickable ? 'notification-item--clickable' : ''}`}
                    onClick={() => isClickable && handleNotificationClick(notification)}
                    onKeyDown={(e) => isClickable && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), handleNotificationClick(notification))}
                  >
                    <div className="notification-icon" style={{ color: style.color }}>
                      {style.icon}
                    </div>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">{formatTimeAgo(notification.createdAt)}</div>
                    </div>
                    {!notification.isRead && <div className="unread-indicator"></div>}
                  </div>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <button 
                className="view-all-btn"
                onClick={() => {
                  closeDropdown();
                  // Navigate to full notifications page if you have one
                  // navigate('/notifications');
                }}
              >
                Ver todas las notificaciones
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
